import { Module } from 'vuex';
import {
  RootState,
  PaywallPlanObject,
  PaywallPlansInfo,
  ChargebeeInfo,
  Optional,
  User,
  GlobalState,
} from 'frontend/types';
import { Topic } from 'frontend/views/premium/PlanComparison.vue';

interface PaywallInfo {
  paywall_plans: PaywallPlanObject[] | null;
  paywall_type: string | null;
  nux_goal: string | null;
  nux_skills_needed: string | null;
  can_skip_paywall: boolean;
  site_name: string;
  api_key: string;
  trial_length: number;
  show_tier_plans: boolean;
  tiered_plan_map: Topic[];
}

interface ExtendedRootState extends RootState {
  globals: GlobalState;
}

function initialState(): PaywallInfo {
  return {
    paywall_plans: null,
    paywall_type: null,
    nux_goal: null,
    nux_skills_needed: null,
    can_skip_paywall: true,
    site_name: '',
    api_key: '',
    trial_length: 7,
    show_tier_plans: false,
    tiered_plan_map: [],
  };
}

const paywall: Module<PaywallInfo, ExtendedRootState> = {
  namespaced: true,
  state: initialState(),

  mutations: {
    setValue(state: object, value: object): void {
      Object.assign(state, value);
    },
  },

  actions: {
    clearPlans({ commit }) {
      commit('setValue', initialState());
    },
  },

  getters: {
    planComparisonTopics(state): Topic[] {
      return state.tiered_plan_map;
    },
    canSkipPaywall(state): boolean {
      return state.can_skip_paywall;
    },
    getChargebeeInfo(state): ChargebeeInfo {
      return { siteName: state.site_name, apiKey: state.api_key };
    },
    hasPaywallPlans(state): boolean {
      return !!state.paywall_plans;
    },
    paywallPlans(state): PaywallPlansInfo {
      const plans: PaywallPlansInfo = {};
      if (state.paywall_plans) {
        // All 3 tier plans are yearly, trial plans
        if (state.show_tier_plans) {
          state.paywall_plans.forEach((plan: PaywallPlanObject) => {
            if (plan.is_math_foundations) {
              plans.annualMathFoundationsPlan = plan;
            } else if (plan.is_core_library) {
              plans.annualCoreLibrary = plan;
            } else if (plan.is_all_access) {
              plans.annualAllAccessPlan = plan;
            }
          });

          return plans;
        }

        for (const plan of state.paywall_plans) {
          plan.is_popular = false;
          switch (plan.interval) {
            case 'year':
              if (plan.is_group) {
                break;
              }
              if (plan.is_math_foundations) {
                plans.annualMathFoundationsPlan = plan;
                break;
              }
              plans.annualPlan = plan;
              if (plan.is_trial) {
                if (plans.annualPlanTrial && plans.annualPlanTrial.is_core_library) {
                  // if we already have a trial plan and it's core library, don't overwrite it
                  break;
                }
                plans.annualPlanTrial = plan;
                plans.annualPlanIsTrialPlan = true;
              } else {
                plans.annualPlanDirect = plan;
              }
              // eslint-disable-next-line camelcase
              if (plans.annualPlan?.includes_referral_discount) {
                plans.annualPlanIncludesReferralDiscount = true;
              }
              break;
            case '3month':
              plans.threeMonthPlan = plan;
              break;
            case '6month':
              plans.sixMonthPlan = plan;
              break;
            case 'month':
              plans.monthlyPlan = plan;
              break;
            case '2year':
              plans.twoYearPlan = plan;
              break;
            case '3year':
              plans.threeYearPlan = plan;
              break;
            case '4year':
              plans.fourYearPlan = plan;
              break;
          }

          if (plan.display_type === 'group_year') {
            plans.groupPlan = plan;
          }

          if (plan.duration === 'lifetime') {
            plans.lifetimePlan = plan;
          }
        }
      }

      if (plans.annualPlan) {
        plans.annualPlan.is_popular = true;
      } else if (plans.sixMonthPlan) {
        plans.sixMonthPlan.is_popular = true;
      } else if (plans.threeMonthPlan) {
        plans.threeMonthPlan.is_popular = true;
      } else if (plans.monthlyPlan) {
        plans.monthlyPlan.is_popular = true;
      }
      return plans;
    },
    trialLength(state): number {
      return state.trial_length;
    },
    hasMathFoundationPlan(_, getters): boolean {
      return (
        !!getters.paywallPlans.annualMathFoundationsPlan &&
        !getters.paywallPlans.annualAllAccessPlan
      );
    },
    hasTieredPlans(_, getters): boolean {
      return !!getters.paywallPlans.annualCoreLibrary && !!getters.paywallPlans.annualAllAccessPlan;
    },
  },
};

interface PrepurchaseDestinationUrl {
  value?: string;
}

export const prepurchaseDestinationUrl: Module<PrepurchaseDestinationUrl, RootState> = {
  namespaced: true,
  state: {
    value: undefined,
  },
  getters: {
    prepurchaseDestinationUrl: (state: PrepurchaseDestinationUrl): Optional<string> => state.value,
  },
  mutations: {
    setValue(state: PrepurchaseDestinationUrl, value: string): void {
      state.value = value;
    },
  },
};

export default paywall;
